import { type FormikErrors, type FormikTouched, setIn } from "formik";
import type { TFunction } from "i18next";
import createClient from "openapi-fetch";

import { IS_COMPANY_ROUTER_FRONTEND, IS_STATIC_DEMO_FRONTEND } from "../globals";
import { request_new_login_action } from "../reducers/login";
import store from "../store";
import type { PaginatedResponse } from "./auto-types/PaginatedResponse";
import type {
  components as CompanyRouterComponents,
  paths as CompanyRouterPaths,
} from "./openapi/company-router";
import type { components as CoreComponents, paths as CorePaths } from "./openapi/core";
import { createStaticDemoCompanyRouterClient, staticDemoCoreClient } from "./static_demo";
import type { Resolve } from "./typechecking";

// Re-exported so consumers reach it through this module's "./static_demo"
// request, which the non-demo builds alias to the disabled stub.
export { demoChatAnswerFor, resolveDemoInstanceIds } from "./static_demo";

type JsonForCode<
  PathDir,
  P extends keyof PathDir,
  M extends keyof PathDir[P],
  C extends number,
> = "responses" extends keyof PathDir[P][M]
  ? C extends keyof PathDir[P][M]["responses"]
    ? "content" extends keyof PathDir[P][M]["responses"][C]
      ? "application/json" extends keyof PathDir[P][M]["responses"][C]["content"]
        ? PathDir[P][M]["responses"][C]["content"]["application/json"]
        : never
      : never
    : never
  : never;

export type CoreJsonForCode<
  Path extends keyof CorePaths,
  Method extends keyof CorePaths[Path],
  Code extends number,
> = Resolve<JsonForCode<CorePaths, Path, Method, Code>>;

export type RouterJsonForCode<
  Path extends keyof CompanyRouterPaths,
  Method extends keyof CompanyRouterPaths[Path],
  Code extends number,
> = Resolve<JsonForCode<CompanyRouterPaths, Path, Method, Code>>;

export const get_company_api_url = () => {
  if (IS_COMPANY_ROUTER_FRONTEND) return "";

  const ret = store.getState().config.company_router_url;
  if (ret === "") throw new Error("Called API function to company router before initialization");
  return ret.replace(/\/+$/, "");
};

const CSRF_HEADER_NAME = "X-CSRF-Token";
const CSRF_COOKIE_NAME = "csrf_token";
const CSRF_PROTECTED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const csrfTokenFromCookie = (): string | null => {
  if (typeof document === "undefined") return null;
  const prefix = `${CSRF_COOKIE_NAME}=`;
  const cookie = document.cookie
    .split(";")
    .map(value => value.trim())
    .find(value => value.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
};

const currentCsrfToken = (): string | null =>
  store.getState().login.user.csrf_token ?? csrfTokenFromCookie();

export const companyRouterFetch: typeof fetch = (input, init) => {
  const method =
    init?.method ??
    (typeof input === "object" && "method" in input ? input.method : undefined) ??
    "GET";
  if (!CSRF_PROTECTED_METHODS.has(method.toUpperCase())) {
    return fetch(input, init);
  }

  const token = currentCsrfToken();
  if (!token) return fetch(input, init);

  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => {
    headers.set(key, value);
  });
  headers.set(CSRF_HEADER_NAME, token);
  return fetch(input, { ...init, headers });
};

export const coreClient = (
  IS_STATIC_DEMO_FRONTEND
    ? staticDemoCoreClient
    : createClient<CorePaths>({
        credentials: "include",
      })
) as ReturnType<typeof createClient<CorePaths>>;

export const createCompanyRouterClient = () =>
  (IS_STATIC_DEMO_FRONTEND
    ? createStaticDemoCompanyRouterClient()
    : createClient<CompanyRouterPaths>({
        baseUrl: get_company_api_url(),
        credentials: "include",
        fetch: companyRouterFetch,
      })) as ReturnType<typeof createClient<CompanyRouterPaths>>;

type ResultLike<T, E> = {
  data?: T;
  error?: E;
  response: Response;
};

type ResponseData<R> = R extends { data?: infer T } ? Exclude<T, undefined> : never;
type ResponseError<R> = R extends { error?: infer E } ? E : never;
type RequireDataResponse<R> = [ResponseData<R>] extends [never]
  ? R & { __use_expect_ok_instead__: never }
  : R;
type RequireNoDataResponse<R> = [ResponseData<R>] extends [never]
  ? R
  : R & { __use_expect_data_instead__: never };
export type ClankApiErrorEnvelope =
  | CoreComponents["schemas"]["ApiErrorEnvelope"]
  | CompanyRouterComponents["schemas"]["ApiErrorEnvelope"];
export type ClankPublicMessage =
  | CoreComponents["schemas"]["PublicMessage"]
  | CompanyRouterComponents["schemas"]["PublicMessage"];
export type ClankResourceLookup =
  | CoreComponents["schemas"]["ResourceLookup"]
  | CompanyRouterComponents["schemas"]["ResourceLookup"];
export type ClankInvalidRequestReason =
  | CoreComponents["schemas"]["InvalidRequestReason"]
  | CompanyRouterComponents["schemas"]["InvalidRequestReason"];
export type ClankPermissionDeniedReason =
  | CoreComponents["schemas"]["PermissionDeniedReason"]
  | CompanyRouterComponents["schemas"]["PermissionDeniedReason"];
export type ClankFormFieldError =
  | CoreComponents["schemas"]["FormFieldError"]
  | CompanyRouterComponents["schemas"]["FormFieldError"];
type RuntimeFormFieldError = ClankFormFieldError | ClankFormFieldError["key"];
type ClankPasswordValidationError =
  | CoreComponents["schemas"]["PasswordValidationError"]
  | CompanyRouterComponents["schemas"]["PasswordValidationError"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isClankApiErrorEnvelope = (value: unknown): value is ClankApiErrorEnvelope =>
  isRecord(value) &&
  isRecord(value.error) &&
  typeof value.error.retryable === "boolean" &&
  typeof value.error.code !== "undefined" &&
  isRecord(value.error.message) &&
  typeof value.error.message.key === "string";

export const apiErrorEnvelope = (error: unknown): ClankApiErrorEnvelope | null => {
  if (error instanceof ApiRequestError && isClankApiErrorEnvelope(error.error)) {
    return error.error;
  }
  if (isClankApiErrorEnvelope(error)) {
    return error;
  }
  return null;
};

const formatResourceLookup = (lookup: ClankResourceLookup, t: TFunction): string => {
  switch (lookup.kind) {
    case "by_field":
      return t("api_error_lookup_by_field", {
        field: lookup.args.field,
        value: lookup.args.value,
      });
    case "by_fields":
      return t("api_error_lookup_by_fields", {
        fields: Object.entries(lookup.args.fields)
          .map(([key, value]) => `${key}: ${value}`)
          .join(", "),
      });
    case "unspecified":
      return t("api_error_lookup_unspecified");
  }
};

const formatInvalidRequestReason = (reason: ClankInvalidRequestReason, t: TFunction): string => {
  switch (reason.key) {
    case "generic":
      return t("invalid_request");
    case "invalid_form":
      return formatInvalidForm(reason.args.fields, t);
    case "invalid_json":
      return t("invalid_json");
    case "invalid_websocket_upgrade":
      return t("invalid_websocket_upgrade");
    case "invalid_path_parameter":
      return t("invalid_path_parameter", { parameter: reason.args.parameter });
    case "invalid_query_parameter":
      return t("invalid_query_parameter", { parameter: reason.args.parameter });
    case "unsupported_value":
      return t("unsupported_value", { field: reason.args.field });
  }
};

/** Each reason's API value is its key in the `permission_denied_reason`
 * translation section, addressed with a per-call `keyPrefix` — typed key
 * resolution folds the option in, so the enum value itself is the
 * typechecked key; only the arg-carrying variants need their own case. */
const formatPermissionDeniedReason = (
  reason: ClankPermissionDeniedReason,
  t: TFunction,
): string => {
  switch (reason.key) {
    case "missing_recording_permission":
      return t("missing_recording_permission", {
        keyPrefix: "permission_denied_reason",
        permission: reason.args.permission,
      });
    case "sync_model_id_forbidden":
      return t("sync_model_id_forbidden", {
        keyPrefix: "permission_denied_reason",
        model: reason.args.model,
      });
    default:
      return t(reason.key, { keyPrefix: "permission_denied_reason" });
  }
};

const normalizeFormFieldError = (error: RuntimeFormFieldError): ClankFormFieldError => {
  if (typeof error === "string") {
    return { key: error } as ClankFormFieldError;
  }
  return error;
};

const formatPasswordValidationError = (
  error: ClankPasswordValidationError,
  t: TFunction,
): string => {
  switch (error.kind) {
    case "too_short":
      return t("form_field_value_too_short", { min: error.args.min_length });
    case "missing_lowercase":
      return t("form_field_password_missing_lowercase");
    case "missing_uppercase":
      return t("form_field_password_missing_uppercase");
    case "missing_digit":
      return t("form_field_password_missing_digit");
    case "missing_symbol":
      return t("form_field_password_missing_symbol");
    case "too_weak":
      return t("form_field_password_too_weak");
  }
};

export const formatFormFieldError = (error: RuntimeFormFieldError, t: TFunction): string => {
  const normalized = normalizeFormFieldError(error);
  switch (normalized.key) {
    case "missing":
    case "required":
      return t("form_field_missing");
    case "invalid_type":
      return t("form_field_invalid_type");
    case "invalid_value":
      return t("form_field_invalid_value");
    case "invalid_foreign_key":
      return t("form_field_invalid_foreign_key");
    case "invalid_email":
      return t("form_field_invalid_email");
    case "invalid_password":
      return normalized.args.reasons
        .map(reason => formatPasswordValidationError(reason, t))
        .join(", ");
    case "value_too_long":
      return t("form_field_value_too_long", { max: normalized.args.max });
    case "value_too_big":
      return t("form_field_value_too_big", { max: normalized.args.max });
    case "value_too_short":
      return t("form_field_value_too_short", { min: normalized.args.min });
    case "value_too_small":
      return t("form_field_value_too_small", { min: normalized.args.min });
  }
};

const formatInvalidForm = (
  fields: Record<string, RuntimeFormFieldError[]>,
  t: TFunction,
): string => {
  const details = Object.entries(fields)
    .map(([field, errors]) =>
      t("invalid_form_field", {
        field,
        errors: errors.map(error => formatFormFieldError(error, t)).join(", "),
      }),
    )
    .join("; ");
  return details ? t("invalid_form_with_fields", { fields: details }) : t("invalid_form");
};

export const formatPublicMessage = (message: ClankPublicMessage, t: TFunction): string => {
  switch (message.key) {
    case "resource_not_found":
      return t("resource_not_found", {
        resource: t(`resource_kind_${message.args.resource}`),
        lookup: formatResourceLookup(message.args.lookup, t),
      });
    case "resource_conflict":
      return t("resource_conflict", {
        resource: t(`resource_kind_${message.args.resource}`),
        lookup: formatResourceLookup(message.args.lookup, t),
      });
    case "permission_denied":
      return formatPermissionDeniedReason(message.args.reason, t);
    case "csrf_validation_failed_missing_header":
      return t("csrf_validation_failed_missing_header");
    case "csrf_validation_failed_invalid_header":
      return t("csrf_validation_failed_invalid_header");
    case "too_many_requests":
      return t("too_many_requests", { details: message.args.details ?? "" });
    case "invalid_request":
      return formatInvalidRequestReason(message.args.reason, t);
    case "authentication_required":
      return t("authentication_required");
    case "conflict":
      return t("conflict");
    case "analysis_already_running":
      return t("analysis_already_running");
    case "service_temporarily_unavailable":
      return t("service_temporarily_unavailable");
    case "answer_too_long":
      return t("answer_too_long");
    case "unexpected_error":
      return t("unexpected_error");
  }
};

export const formatApiRequestError = (error: unknown, t: TFunction): string => {
  const envelope = apiErrorEnvelope(error);
  if (envelope) {
    return formatPublicMessage(envelope.error.message, t);
  }
  return error instanceof Error ? error.message : t("request_failed");
};

export type ApiFormErrorSplit<FormValues extends Record<string, unknown>> = {
  fieldErrors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  remainder: string | null;
};

export const splitApiRequestFormErrors = <FormValues extends Record<string, unknown>>(
  error: unknown,
  t: TFunction,
  formFields: readonly string[],
  fieldAliases: Partial<Record<string, string>> = {},
): ApiFormErrorSplit<FormValues> => {
  const envelope = apiErrorEnvelope(error);
  const message = envelope?.error.message;
  if (message?.key !== "invalid_request" || message.args.reason.key !== "invalid_form") {
    return { fieldErrors: {}, touched: {}, remainder: formatApiRequestError(error, t) };
  }

  const formFieldSet = new Set(formFields);
  let fieldErrors: FormikErrors<FormValues> = {};
  let touched: FormikTouched<FormValues> = {};
  const remainderFields: Record<string, RuntimeFormFieldError[]> = {};

  for (const [field, errors] of Object.entries(message.args.reason.args.fields) as [
    string,
    RuntimeFormFieldError[],
  ][]) {
    const formField = formFieldSet.has(field) ? field : fieldAliases[field];
    if (formField) {
      fieldErrors = setIn(
        fieldErrors,
        formField,
        errors.map(error => formatFormFieldError(error, t)).join(", "),
      );
      touched = setIn(touched, formField, true);
    } else {
      remainderFields[field] = errors;
    }
  }

  return {
    fieldErrors,
    touched,
    remainder:
      Object.keys(remainderFields).length > 0 ? formatInvalidForm(remainderFields, t) : null,
  };
};

const formatError = (error: unknown): string => {
  if (isClankApiErrorEnvelope(error)) return error.error.message.key;
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object") return JSON.stringify(error);
  return "Request failed";
};

export class ApiRequestError<E = unknown> extends Error {
  response: Response;
  error: E | undefined;

  constructor(message: string, response: Response, error: E | undefined) {
    super(message);
    this.name = "ApiRequestError";
    this.response = response;
    this.error = error;
  }

  get status(): number {
    return this.response.status;
  }
}

export const isApiRequestError = (error: unknown): error is ApiRequestError =>
  error instanceof ApiRequestError;

const makeApiRequestError = <E>(
  result: { error?: E; response: Response },
  fallbackMessage: string,
): ApiRequestError<E> =>
  new ApiRequestError(formatError(result.error) || fallbackMessage, result.response, result.error);

export const expectData = <R extends { data?: unknown; error?: unknown; response: Response }>(
  result: RequireDataResponse<R>,
  fallbackMessage: string,
): ResponseData<R> => {
  if (result.data !== undefined) return result.data as ResponseData<R>;
  if (!result.response.ok) {
    if (result.response.status === 401) {
      store.dispatch(request_new_login_action());
    }

    throw makeApiRequestError(result, fallbackMessage);
  }
  throw new Error(fallbackMessage);
};

export const expectOk = <R extends { data?: unknown; error?: unknown; response: Response }>(
  result: RequireNoDataResponse<R>,
  fallbackMessage: string,
): void => {
  if (result.response.ok) return;
  if (result.response.status === 401) {
    store.dispatch(request_new_login_action());
  }
  throw makeApiRequestError(result as ResultLike<never, ResponseError<R>>, fallbackMessage);
};

export const get_all_paginated_pages = async <T, E = unknown>(
  fetchPage: (page: number) => Promise<PaginatedResponse<T, E>>,
): Promise<{ items: T[]; extra: E }> => {
  const first = await fetchPage(1);
  let { items, extra } = first;

  for (let page = 2; page <= first.total_pages; page++) {
    const others = await fetchPage(page);
    items = items.concat(others.items);
  }

  return { items, extra };
};
